/**
 * @file AWS Integration Details Step Component
 * @description First step in the AWS integration wizard. Collects account-level information including
 * integration type (account vs organization level), account/organization ID, and friendly label.
 */

import React, { useState, useEffect } from 'react';
import { Button, FormGroup, Input, Label } from 'reactstrap';

import { validateText } from '../../../../../../../../lib/inputValidator';
import { StepComponentProps } from '../StepperModal';
import { IntegrationType } from '../types';

/**
 * AWSIntegrationDetailsStep - Collects AWS account details
 *
 * This component is the first step in the AWS integration wizard. It allows users to:
 * 1. Choose integration type (Account Level vs Organization Level)
 * 2. Enter AWS Organization ID (if organization level selected)
 * 3. Provide a friendly name for the integration
 *
 * Integration Level Options:
 * - ACCOUNT_LEVEL: Monitor a single AWS account
 * - ORGANIZATION_LEVEL: Monitor multiple accounts under one AWS organization
 *
 * Validation:
 * - Integration label is required
 * - Integration type must be selected
 * - Organization ID required only for ORGANIZATION_LEVEL
 *
 * @component
 * @param {StepComponentProps} props - Step component props
 * @returns {React.ReactElement} Form for collecting AWS account details
 */
const AWSIntegrationDetailsStep: React.FC<StepComponentProps> = ({
  description,
  data,
  onValidationChange,
  onNext,
  onBack,
  loading,
  allowBack,
}) => {
  // Form field states
  const [integrationLabel, setIntegrationLabel] = useState(
    data.integrationLabel || '',
  );
  const [accountId, setAccountId] = useState(data.accountId || '');
  const [selectedIntegrationType, setSelectedIntegrationType] =
    useState<IntegrationType>(
      (data.integrationType as IntegrationType) ||
        IntegrationType.ACCOUNT_LEVEL,
    );
  const [validationErrors, setValidationErrors] = useState<{
    integrationLabel?: string;
    accountId?: string;
  }>({});

  /**
   * Handles integration type selection
   * Clears account ID when switching from organization to account level
   * @param {IntegrationType} type - Selected integration type
   */
  const handleIntegrationTypeChange = (type: IntegrationType) => {
    setSelectedIntegrationType(type);
    // Clear accountId when switching to ACCOUNT_LEVEL (not needed for single account)
    if (type === IntegrationType.ACCOUNT_LEVEL) {
      setAccountId('');
      setValidationErrors((prev) => ({ ...prev, accountId: undefined }));
    }
  };

  /**
   * Notify parent of validation state whenever form fields change
   * Validation: Integration label and type required, organization ID required for ORGANIZATION_LEVEL
   */
  useEffect(() => {
    const isValid =
      integrationLabel.trim() !== '' &&
      selectedIntegrationType !== undefined &&
      (selectedIntegrationType !== IntegrationType.ORGANIZATION_LEVEL ||
        accountId.trim() !== '') &&
      !validationErrors.integrationLabel &&
      !validationErrors.accountId;

    onValidationChange(isValid);
  }, [
    integrationLabel,
    selectedIntegrationType,
    accountId,
    validationErrors,
    onValidationChange,
  ]);

  /**
   * Handles form submission and navigation to next step
   * Validates required fields before proceeding
   */
  const handleNext = () => {
    // Validation: Check required fields
    if (
      !integrationLabel.trim() ||
      !selectedIntegrationType ||
      (selectedIntegrationType === IntegrationType.ORGANIZATION_LEVEL &&
        !accountId.trim()) ||
      validationErrors.integrationLabel ||
      validationErrors.accountId
    ) {
      return; // Don't proceed if validation fails
    }

    // Pass data to next step
    onNext({
      ...data,
      integrationLabel: integrationLabel,
      accountId,
      integrationType: selectedIntegrationType,
    });
  };

  // Configuration for integration type options
  const integrationTypes = [
    {
      value: IntegrationType.ACCOUNT_LEVEL,
      label: 'Account Level',
      description:
        'Integrate at the account level, allowing access to all resources within the selected AWS account.',
    },
    {
      value: IntegrationType.ORGANIZATION_LEVEL,
      label: 'Organization Level',
      description:
        'Integrate at the organization level, allowing access to multiple AWS accounts under a single organization.',
    },
  ];

  return (
    <div className="step-content-wrapper">
      {/* Zone 1 – Step description (pinned header) */}
      <div className="step-description-header">
        <h5>{description ?? 'Integration Details'}</h5>
      </div>

      {/* Zone 2 – Scrollable form body */}
      <div className="step-form-body">
        {/* Integration Type Selection */}
        <FormGroup>
          <Label for="accountType">Integration Level *</Label>
          {integrationTypes.map((accountType) => (
            <div
              key={accountType.value}
              className={`provider-card mb-2 p-2 border rounded ${
                selectedIntegrationType === accountType.value
                  ? 'border-primary bg-light'
                  : ''
              }`}
              style={{ cursor: 'pointer' }}
              onClick={() => handleIntegrationTypeChange(accountType.value)}
              role="radio"
              aria-checked={selectedIntegrationType === accountType.value}
              tabIndex={0}
              onKeyPress={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleIntegrationTypeChange(accountType.value);
                }
              }}
            >
              <div className="d-flex align-items-start">
                <div className="mr-2 mt-1">
                  <input
                    type="radio"
                    name="accountType"
                    id={`accountType-${accountType.value}`}
                    value={accountType.value}
                    checked={selectedIntegrationType === accountType.value}
                    onChange={() =>
                      handleIntegrationTypeChange(accountType.value)
                    }
                    aria-label={accountType.label}
                    tabIndex={-1}
                  />
                </div>
                <div className="flex-grow-1">
                  <Label
                    htmlFor={`accountType-${accountType.value}`}
                    className="mb-0"
                    style={{ cursor: 'pointer', fontWeight: 'bold' }}
                  >
                    {accountType.label}
                  </Label>
                  <div className="text-muted" style={{ fontSize: '0.875rem' }}>
                    {accountType.description}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </FormGroup>

        {/* Conditional: Organization ID field (only for ORGANIZATION_LEVEL) */}
        {selectedIntegrationType === IntegrationType.ORGANIZATION_LEVEL && (
          <FormGroup>
            <Label for="awsOrgID">AWS Organization ID *</Label>
            <Input
              type="text"
              id="awsOrgID"
              placeholder="Enter your AWS Organization ID"
              value={accountId}
              onChange={(e) => {
                const val = e.target.value;
                setAccountId(val);
                // AWS Org ID starts with 'o-' followed by 10 to 32 lower-case letters or digits
                // Since user might want to provide custom regex per prompt, here we allow a regex or fallback.
                // Using a specific regex for AWS Organization ID or standard text validate.
                const validatorResult = validateText(val, {
                  maxLength: 34,
                  regex: /^o-[a-z0-9]{10,32}$/i,
                });

                if (val && !validatorResult.isValid) {
                  setValidationErrors((prev) => ({
                    ...prev,
                    accountId:
                      'Invalid AWS Organization ID format (e.g. o-xxxxxxxxxx)',
                  }));
                } else {
                  setValidationErrors((prev) => ({
                    ...prev,
                    accountId: undefined,
                  }));
                }
              }}
              disabled={loading}
              className={
                validationErrors.accountId ? 'input-error-highlight' : ''
              }
            />
            {validationErrors.accountId && (
              <div className="invalid-feedback d-block text-danger">
                {validationErrors.accountId}
              </div>
            )}
          </FormGroup>
        )}

        {/* Integration Label */}
        <FormGroup>
          <Label for="integrationLabel">Integration Name *</Label>
          <Input
            type="text"
            id="integrationLabel"
            placeholder="Enter a descriptive integration name (e.g., Production Account)."
            value={integrationLabel}
            onChange={(e) => {
              const val = e.target.value;
              setIntegrationLabel(val);
              const result = validateText(val, { maxLength: 100 });
              if (val && !result.isValid) {
                setValidationErrors((prev) => ({
                  ...prev,
                  integrationLabel: result.errorMessage,
                }));
              } else {
                setValidationErrors((prev) => ({
                  ...prev,
                  integrationLabel: undefined,
                }));
              }
            }}
            disabled={loading}
            className={
              validationErrors.integrationLabel ? 'input-error-highlight' : ''
            }
          />
          {validationErrors.integrationLabel && (
            <div className="invalid-feedback d-block text-danger">
              {validationErrors.integrationLabel}
            </div>
          )}
        </FormGroup>
      </div>

      {/* Zone 3 – Action buttons (pinned footer) */}
      <div
        className={`step-action-footer d-flex ${
          allowBack ? 'justify-content-between' : 'justify-content-end'
        }`}
      >
        {allowBack && (
          <Button color="secondary" onClick={onBack} disabled={loading}>
            Back
          </Button>
        )}
        <Button
          color="primary"
          onClick={handleNext}
          disabled={
            !integrationLabel.trim() ||
            !selectedIntegrationType ||
            (selectedIntegrationType === IntegrationType.ORGANIZATION_LEVEL &&
              !accountId.trim()) ||
            !!validationErrors.integrationLabel ||
            !!validationErrors.accountId ||
            loading
          }
        >
          {loading ? 'Processing...' : 'Next'}
        </Button>
      </div>
    </div>
  );
};

export default AWSIntegrationDetailsStep;
